import React, { useState } from "react";
import { Form, Input, Button, Card, Spin, Alert } from "antd";
import {
  UserOutlined,
  LockOutlined,
  LoadingOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { Link, Navigate, useNavigate } from "react-router-dom";
import logoPng from "../../assets/images/logopng.png";
import chart from "../../assets/images/dashboard.gif";
import Theme from "../../theme/Theme";
import { useDispatch, useSelector } from "react-redux";
import LoginApi from "../../api/auth/LoginApi";
import Verify2FAApi from "../../api/auth/Verify2FAApi";
import Resend2FAApi from "../../api/auth/Resend2FAApi";
import { login, logout } from "../../features/AuthSlice";
import { firstLogin } from "../../features/FirstLoginSlice";
import { GetMeApi } from "../../api/users/GetMeApi";
import { userDetails } from "../../features/UserSlice";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { positionDetails } from "../../features/PositionSlice";

function Login() {
  const [submit, setSubmit] = useState(false);
  const [step, setStep] = useState("login"); // "login" | "2fa" | "locked"
  const [lockedMessage, setLockedMessage] = useState("");
  const [twoFAUsername, setTwoFAUsername] = useState("");
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const authState = useSelector((state) => state.authState.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [form2FA] = Form.useForm();
  const [positions, setPositions] = useState([]);

  if (authState === "login") {
    return <Navigate to={"/metabase"} replace />;
  }

  function showErrorMessage(message) {
    if (message === "Invalid credentials") {
      form.setFields([
        {
          name: "username",
          errors: [""],
        },
        {
          name: "password",
          errors: ["Username and password didn't match"],
        },
      ]);
    } else if (message === "User not found") {
      form.setFields([
        {
          name: "username",
          errors: ["User not found"],
        },
      ]);
    } else if (message?.includes("attempt")) {
      form.setFields([
        {
          name: "password",
          errors: [message],
        },
      ]);
    } else {
      form.setFields([
        {
          name: "username",
          errors: [""],
        },
        {
          name: "password",
          errors: ["Couldn't login. Try again later."],
        },
      ]);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    const apiResponse = await LoginApi(values.username, values.password);

    if (apiResponse.error === "Forbidden") {
      // Account locked — check the message for reason
      setLockedMessage(apiResponse.message);
      setStep("locked");
    } else if (apiResponse.error === "Not Found") {
      showErrorMessage(apiResponse.message);
    } else if (apiResponse.error === "Bad Request") {
      showErrorMessage(apiResponse.message);
    } else if (apiResponse.requires2FA) {
      // 2FA required
      setTwoFAUsername(values.username);
      setPasswordExpired(apiResponse.passwordExpired || false);
      setStep("2fa");
      if (apiResponse.devCode) {
        openNotificationSuccess(
          "bottomRight",
          `Dev mode - Your code: ${apiResponse.devCode}`
        );
      } else {
        openNotificationSuccess(
          "bottomRight",
          "Verification code sent to your email"
        );
      }
      // Show expiry warning if applicable
      if (apiResponse.showExpiryWarning && apiResponse.daysUntilExpiry) {
        const days = apiResponse.daysUntilExpiry;
        setTimeout(() => {
          openNotificationError(
            "bottomRight",
            `Your password will expire in ${days} day${days === 1 ? '' : 's'}. Please change it after logging in.`
          );
        }, 1500);
      }
    } else if (apiResponse.data?.customerAccessToken) {
      // Direct login (shouldn't happen with 2FA enabled but fallback)
      await handleSuccessfulLogin(apiResponse);
    } else {
      showErrorMessage("Something is wrong");
    }
    setSubmit(false);
  }

  async function onVerify2FA(values) {
    setSubmit(true);
    const apiResponse = await Verify2FAApi(twoFAUsername, values.code);

    if (apiResponse.error === "Bad Request") {
      form2FA.setFields([
        {
          name: "code",
          errors: [apiResponse.message],
        },
      ]);
    } else if (apiResponse.data?.customerAccessToken) {
      await handleSuccessfulLogin(apiResponse);
    } else {
      form2FA.setFields([
        {
          name: "code",
          errors: ["Verification failed. Please try again."],
        },
      ]);
    }
    setSubmit(false);
  }

  async function handleResend() {
    setResending(true);
    const apiResponse = await Resend2FAApi(twoFAUsername);
    if (apiResponse.success) {
      openNotificationSuccess(
        "bottomRight",
        "A new verification code has been sent to your email"
      );
    } else {
      openNotificationError(
        "bottomRight",
        "Failed to resend code. Please try again."
      );
    }
    setResending(false);
  }

  async function handleSuccessfulLogin(apiResponse) {
    localStorage.setItem("authToken", apiResponse.data?.customerAccessToken);
    localStorage.setItem("authUser", apiResponse.data?.username);
    getUserDetails();
    dispatch(login());

    // Show password expiry warning if applicable
    if (apiResponse.data?.showExpiryWarning && apiResponse.data?.daysUntilExpiry) {
      const days = apiResponse.data.daysUntilExpiry;
      openNotificationError(
        "bottomRight",
        `Your password will expire in ${days} day${days === 1 ? '' : 's'}. Please change it soon.`
      );
    }

    if (apiResponse.data?.firstLogin) {
      dispatch(firstLogin(true));
      navigate("/setup-profile");
    } else if (!apiResponse.data?.firstLogin && positions.length === 0) {
      navigate("/not-found");
    } else {
      navigate(`/metabase`);
    }
  }

  async function getUserDetails() {
    const apiResponse = await GetMeApi();
    if (apiResponse.error === "Unauthorized") {
      openNotificationError("bottoRight", apiResponse.message);
      localStorage.setItem("authToken", "");
      localStorage.setItem("authUser", "");
      dispatch(logout());
    } else if (apiResponse.success) {
      dispatch(userDetails(apiResponse.data));
      const lastPosition = localStorage.getItem("stPosition");
      if (lastPosition) {
        const position = apiResponse.data?.positions.find(
          (pos) => pos.id === lastPosition
        );
        if (position) {
          dispatch(positionDetails(position));
        } else {
          dispatch(positionDetails(apiResponse.data?.positions[0]));
          localStorage.setItem(
            "stPosition",
            apiResponse.data?.positions[0]?.id
          );
        }
      } else {
        dispatch(positionDetails(apiResponse.data?.positions[0]));
        localStorage.setItem("stPosition", apiResponse.data?.positions[0]?.id);
      }
      setPositions(apiResponse.data?.positions);
    } else {
      localStorage.setItem("authToken", "");
      localStorage.setItem("authUser", "");
      dispatch(logout());
    }
  }

  function renderLockedState() {
    return (
      <div className="text-center">
        <Alert
          message="Account Locked"
          description={lockedMessage}
          type="error"
          showIcon
          className="mb-6 text-left"
        />
        <div className="mb-4 text-gray-600">
          To unlock your account, please reset your password using the link
          below.
        </div>
        <Link to="/forgot-password">
          <Button type="primary" size="large" className="w-full mb-4">
            Reset Password
          </Button>
        </Link>
        <Button
          type="link"
          onClick={() => {
            setStep("login");
            setLockedMessage("");
            form.resetFields();
          }}
        >
          Back to login
        </Button>
      </div>
    );
  }

  function render2FAState() {
    return (
      <div>
        <div className="text-center mb-6">
          <div className="text-4xl text-blue-500 mb-3">
            <SafetyOutlined />
          </div>
          <div className="pri-font text-xl font-semibold mb-1">
            Verify Your Identity
          </div>
          <div className="text-gray-500 text-sm">
            We've sent a 6-digit verification code to your email. Please enter
            it below.
          </div>
        </div>
        <Form
          name="verify2fa"
          layout="vertical"
          form={form2FA}
          onFinish={onVerify2FA}
          requiredMark={false}
        >
          <Form.Item
            label={<div className="text-gray-600">Verification Code</div>}
            name="code"
            rules={[
              {
                required: true,
                message: "Please enter the verification code!",
              },
              {
                len: 6,
                message: "Code must be 6 digits",
              },
            ]}
          >
            <Input
              prefix={<SafetyOutlined style={{ color: Theme().primary }} />}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="text-center text-lg"
              style={{ letterSpacing: "4px" }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              disabled={submit}
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={
                    <LoadingOutlined style={{ color: "#fff" }} spin />
                  }
                />
              ) : (
                "Verify"
              )}
            </Button>
          </Form.Item>
        </Form>
        <div className="text-center mt-3">
          <Button
            type="link"
            onClick={handleResend}
            disabled={resending}
            size="small"
          >
            {resending ? "Sending..." : "Didn't receive the code? Resend"}
          </Button>
        </div>
        <div className="text-center mt-1">
          <Button
            type="link"
            size="small"
            onClick={() => {
              setStep("login");
              setTwoFAUsername("");
              form.resetFields();
              form2FA.resetFields();
            }}
          >
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  function renderLoginForm() {
    return (
      <>
        <div>
          <div className="pri-font text-2xl font-semibold mb-2 mt-4">
            Welcome back
          </div>
          <div className="text-gray-600 mb-5">Login to your account.</div>
        </div>
        <Form
          name="login"
          className="w-full max-w-lg"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          requiredMark={false}
        >
          <Form.Item
            label={<div className="text-gray-600">Username</div>}
            name="username"
            rules={[
              {
                required: true,
                message: "Please enter username!",
              },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: Theme().primary }} />}
              placeholder="Username"
            />
          </Form.Item>
          <Form.Item
            label={<div className="text-gray-600">Password</div>}
            name="password"
            rules={[
              {
                required: true,
                message: "Please enter password!",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: Theme().primary }} />}
              type="password"
              placeholder="Password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              disabled={submit}
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={
                    <LoadingOutlined style={{ color: "#fff" }} spin />
                  }
                />
              ) : (
                "Log in"
              )}
            </Button>
          </Form.Item>
        </Form>
        <div className="text-center mt-5 mb-4">
          Forgot your password?{" "}
          <Link to="/forgot-password">Reset password</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="flex w-full h-full items-center justify-center p-3"
        style={{ backgroundColor: "#f1f1f1" }}
      >
        <div className=" max-w-lg md:max-w-3xl md:p-3  md:grid  grid-cols-2 bg-white rounded-md">
          <div className="bg-gray-100 p-8 rounded-md hidden md:block">
            <img className="w-48 " src={logoPng} alt="logo" />
            <div className="pri-font text-2xl font-semibold mt-16 mb-4">
              Let us help you manage business performance.
            </div>
            <div className="text-gray-600 mb-10">
              We'll help you monitor your corporate performance through data
              visualization.
            </div>
            <img
              className="h-44 mx-auto w-auto rounded-md overflow-hidden"
              src={chart}
              alt="chart"
            />
          </div>
          <div className="  p-8 flex flex-col justify-between">
            {step === "login" && renderLoginForm()}
            {step === "2fa" && render2FAState()}
            {step === "locked" && renderLockedState()}
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
